import React, { useEffect, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useLazyGetRefundInitialDataQuery,
    useCreateRefundDraftMutation
} from '../../../../api/endpoints/vehicleRefundOrder';
import { useGetLookUpDropdownListQuery } from '../../../../api/endpoints/lookup';
import { toastService } from '../../../../utils/toastService';

const CreateRefundDialog = ({ visible, row, onHide, onActionComplete }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [initialData, setInitialData] = useState(null);
    const [refundReasonCode, setRefundReasonCode] = useState('');
    const [note, setNote] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);

    const [fetchInitialData] = useLazyGetRefundInitialDataQuery();
    const [createDraft, { isLoading: isCreating }] = useCreateRefundDraftMutation();

    const { data: refundReasons = [] } = useGetLookUpDropdownListQuery(
        { GroupCode: 'IADE_SIPARIS_NEDENI', LanguageId: languageId },
        { skip: !visible }
    );

    const resetForm = useCallback(() => {
        setInitialData(null);
        setRefundReasonCode('');
        setNote('');
        setValidationErrors({});
        setIsLoadingInitial(false);
    }, []);

    useEffect(() => {
        if (visible && row) {
            resetForm();
            setIsLoadingInitial(true);
            fetchInitialData({ vehicleStockId: row.id, languageId })
                .unwrap()
                .then((data) => {
                    if (data) {
                        setInitialData(data);
                    } else {
                        onHide();
                    }
                })
                .catch(() => {
                    onHide();
                })
                .finally(() => {
                    setIsLoadingInitial(false);
                });
        }
    }, [visible, row]);

    if (!row) return null;

    const validateForm = () => {
        const errors = {};
        if (!refundReasonCode) {
            errors.refundReasonCode = th('vehicleRefundOrders.validation.refundReasonRequired');
        }
        if (!note?.trim()) {
            errors.note = th('vehicleRefundOrders.validation.noteRequired');
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!initialData) return;
        if (!validateForm()) return;
        try {
            const body = {
                vehicleStockId: row.id,
                refundReasonCode,
                note: note.trim(),
                languageId
            };

            const res = await createDraft(body).unwrap();
            if (res?.isSuccessful || res?.isSuccess) {
                toastService.success(res.message || th('vehicleRefundOrders.messages.draftCreated'));
                onActionComplete?.();
                handleClose();
            } else {
                toastService.error(res?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
            }
        } catch (err) {
            toastService.error(err?.data?.errorMessage || err?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
        }
    };

    const handleClose = () => {
        resetForm();
        onHide();
    };

    const reasonOptions = refundReasons.map((r) => ({
        label: r.translatedText || r.text || r.description || r.name || r.code,
        value: r.code
    }));

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('vehicleRefundOrders.buttons.cancel')}
                icon="pi pi-times"
                className="p-button-outlined"
                onClick={handleClose}
                disabled={isCreating}
            />
            <Button
                label={th('vehicleRefundOrders.buttons.save')}
                icon="pi pi-save"
                onClick={handleSave}
                loading={isCreating}
                disabled={!initialData}
            />
        </div>
    );

    const readonlyField = (label, value) => (
        <div className="col-12 md:col-6">
            <div className="field">
                <label className="block mb-1 text-sm font-medium">{label}</label>
                <InputText value={value ?? '-'} readOnly className="w-full" />
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header={th('vehicleRefundOrders.createDialog.title')}
            style={{ width: '70vw', maxWidth: '800px' }}
            modal
            footer={isLoadingInitial ? null : footer}
            closable={!isCreating}
            maximizable
        >
            {isLoadingInitial ? (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner />
                </div>
            ) : initialData ? (
                <div className="grid">
                    <div className="col-12">
                        <h5 className="mt-2">{th('vehicleRefundOrders.createDialog.orderInfo')}</h5>
                    </div>

                    {readonlyField(th('vehicleRefundOrders.createDialog.dmsOrderNumber'), initialData.dmsOrderNumber)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.customerNumber'), initialData.kunnr)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.chassisNo'), initialData.chassisNo)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.matnr'), initialData.matnr)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.maktx'), initialData.maktx)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.brand'), initialData.brand)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.model'), initialData.model)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.partyNumber'), initialData.partyNumber)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.deliveryDate'), initialData.deliveryDate)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.currency'), initialData.currency)}
                    {readonlyField(th('vehicleRefundOrders.createDialog.meins'), initialData.meins || 'ADT')}
                    {readonlyField(th('vehicleRefundOrders.createDialog.quantity'), String(initialData.quantity ?? 1))}

                    <div className="col-12">
                        <h5 className="mt-2">{th('vehicleRefundOrders.createDialog.refundInfo')}</h5>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label className="block mb-1 text-sm font-medium">
                                {th('vehicleRefundOrders.createDialog.refundReason')} <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={refundReasonCode}
                                options={reasonOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => {
                                    setRefundReasonCode(e.value);
                                    if (e.value) setValidationErrors((prev) => ({ ...prev, refundReasonCode: undefined }));
                                }}
                                placeholder={th('vehicleRefundOrders.createDialog.refundReasonPlaceholder')}
                                className={`w-full ${validationErrors.refundReasonCode ? 'p-invalid' : ''}`}
                                filter
                                showClear
                            />
                            {validationErrors.refundReasonCode && (
                                <small className="p-error">{validationErrors.refundReasonCode}</small>
                            )}
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label className="block mb-1 text-sm font-medium">
                                {th('vehicleRefundOrders.createDialog.note')} <span className="text-red-500">*</span>
                            </label>
                            <InputTextarea
                                value={note}
                                onChange={(e) => {
                                    setNote(e.target.value);
                                    if (e.target.value?.trim()) setValidationErrors((prev) => ({ ...prev, note: undefined }));
                                }}
                                rows={4}
                                className={`w-full ${validationErrors.note ? 'p-invalid' : ''}`}
                                placeholder={th('vehicleRefundOrders.createDialog.notePlaceholder')}
                                autoResize
                            />
                            {validationErrors.note && (
                                <small className="p-error">{validationErrors.note}</small>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </Dialog>
    );
};

export default CreateRefundDialog;
